import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, Send } from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contacts`, form);
      toast.success("Message sent! We'll respond shortly.");
      setForm(initialState);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not send message. Try again.";
      toast.error(typeof detail === "string" ? detail : "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-36 px-6 lg:px-10"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-red-600" />
            <span className="text-xs uppercase tracking-[0.3em] text-red-600">
              Get in Touch
            </span>
          </div>
          <h2
            data-testid="contact-title"
            className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-10"
          >
            Questions? <br />
            <span className="text-red-600">Talk to a Coach.</span>
          </h2>

          <div className="space-y-6">
            <ContactItem
              icon={MapPin}
              label="Visit"
              value="Witbank, eMalahleni · Mpumalanga, South Africa"
            />
            <ContactItem
              icon={WhatsAppIcon}
              label="WhatsApp"
              value="+27 65 904 4297"
              href="https://wa.me/27659044297"
              external
            />
            <ContactItem
              icon={Mail}
              label="Email"
              value="randy@starsofmp.co.za"
              href="mailto:randy@starsofmp.co.za"
            />
          </div>

          <div className="mt-10 rounded-xl border border-white/10 bg-neutral-950 p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-red-600 mb-3">
              Training Hours
            </p>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex justify-between">
                <span>Mon – Fri</span>
                <span className="text-white">16:00 – 19:30</span>
              </li>
              <li className="flex justify-between">
                <span>Saturday</span>
                <span className="text-white">08:00 – 13:00</span>
              </li>
              <li className="flex justify-between">
                <span>Sunday</span>
                <span className="text-white/40">Match Day · Off</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7 fade-up">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="rounded-2xl border border-white/10 bg-neutral-950 p-6 lg:p-10 space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Your Name *"
                id="name"
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Thandi Mahlangu"
              />
              <Field
                label="Email *"
                id="email"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                placeholder="you@email.com"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Phone"
                id="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="+27 ..."
              />
              <Field
                label="Subject"
                id="subject"
                value={form.subject}
                onChange={handleChange("subject")}
                placeholder="Trial session"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-white/60">
                Message *
              </Label>
              <Textarea
                data-testid="contact-message"
                value={form.message}
                onChange={handleChange("message")}
                rows={6}
                placeholder="How can we help?"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-red-600"
              />
            </div>

            <Button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-heading uppercase tracking-widest rounded-full h-14 text-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending
                </>
              ) : (
                <>
                  Send Message <Send className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, label, value, href, external }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-600/10 text-red-600 flex-shrink-0">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
          {label}
        </p>
        <p className="text-base text-white group-hover:text-red-600 transition-colors">
          {value}
        </p>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      className="block"
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={`contact-item-${label.toLowerCase()}`}
    >
      {content}
    </a>
  ) : (
    content
  );
}

function Field({ label, id, type = "text", value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-widest text-white/60"
      >
        {label}
      </Label>
      <Input
        id={id}
        data-testid={`contact-${id}`}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-red-600 h-11"
      />
    </div>
  );
}
